import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  TrophyIcon,
  ReceiptPercentIcon,
  CalendarDaysIcon,
  StarIcon,
  CheckCircleIcon,
  Square2StackIcon,
  PencilSquareIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ArrowsRightLeftIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useCamper } from "../hooks/useCampers";
import { useAuth } from "../hooks/useAuth";
import PageHeader from "../components/layout/PageHeader";
import { Card, CardHeader } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Avatar } from "../components/ui/Avatar";
import { Modal } from "../components/ui/Modal";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import CompletionForm from "../components/forms/CompletionForm";
import HonorForm from "../components/forms/HonorForm";
import CamperForm from "../components/forms/CamperForm";
import CamperMergeForm from "../components/forms/CamperMergeForm";
import EnrollmentForm from "../components/forms/EnrollmentForm";
import OverrideCabinDialog from "../components/forms/OverrideCabinDialog";
import { reportsApi } from "../api/reports";
import { rankingsApi } from "../api/rankings";
import { campersApi } from "../api/campers";
import { useSeason } from "../context/SeasonContext";
import { formatCabinNumber } from "../utils/terminology";
import type { Enrollment, Cabin, CamperKerchiefData, KerchiefActivity, CamperRankEntry } from "../types";
import { sortAcs } from "../utils/acSort";

// ─── Enrollment grouping ───────────────────────────────────────────────────────

interface EnrollmentGroup {
  year: number;
  isFullTerm: boolean;
  termLabel: string;
  cabin?: Cabin;
  ids: number[];
  /** Present only when not full term (enables editing) */
  singleEnrollment?: Enrollment;
  /**
   * For full-term groups: each underlying bunk-plan enrollment, sorted by term.
   * Cabin overrides are stored per-enrollment, so admins can move a camper for
   * one term without affecting the other.
   */
  termEnrollments?: Enrollment[];
}

function buildEnrollmentGroups(enrollments: Enrollment[]): EnrollmentGroup[] {
  const byYear = new Map<number, Enrollment[]>();
  for (const e of enrollments) {
    const list = byYear.get(e.year) ?? [];
    list.push(e);
    byYear.set(e.year, list);
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, group]) => {
      // Bunk-plan terms (campMinderBunkPlanId set) = real session terms (1st/2nd Term).
      // Session-only terms (Rangers etc.) have campMinderBunkPlanId = null and must not
      // count toward Full Term detection — matching the reportService.ts logic.
      const bunkPlanEnrollments = group.filter((e) => e.termId != null && e.term?.campMinderBunkPlanId != null);
      // 2+ bunk-plan enrollments → Full Term
      if (bunkPlanEnrollments.length > 1) {
        const sortedTerms = [...bunkPlanEnrollments].sort((a, b) => {
          const aStart = a.term?.startDate ?? "";
          const bStart = b.term?.startDate ?? "";
          if (aStart && bStart && aStart !== bStart) return aStart.localeCompare(bStart);
          return (a.term?.name ?? "").localeCompare(b.term?.name ?? "");
        });
        return {
          year,
          isFullTerm: true,
          termLabel: "Full Term",
          cabin: bunkPlanEnrollments[0].cabin,
          ids: group.map((e) => e.id),
          termEnrollments: sortedTerms,
        };
      }
      // 1 bunk-plan enrollment (+ possibly Rangers / year-level rows) → show that term
      if (bunkPlanEnrollments.length === 1) {
        const termEnrollment = bunkPlanEnrollments[0];
        return {
          year,
          isFullTerm: false,
          termLabel: termEnrollment.term?.name ?? "No term",
          cabin: termEnrollment.cabin,
          ids: group.map((e) => e.id),
          singleEnrollment: termEnrollment,
        };
      }
      // No bunk-plan terms — session-only (e.g. Rangers) or year-level historical row
      const single = group.filter((e) => e.termId != null)[0] ?? group[0];
      return {
        year,
        isFullTerm: false,
        termLabel: single.term?.name ?? "No term",
        cabin: single.cabin,
        ids: group.map((e) => e.id),
        singleEnrollment: single,
      };
    });
}

const CURRENT_YEAR = new Date().getFullYear();

// ─── Rankings block (admin/staff only) ────────────────────────────────────────

const METRIC_LABEL: Record<string, string> = {
  acs: "ACs",
  honors: "Honors",
  graduations: "Graduations",
};
const SCOPE_LABEL: Record<string, string> = {
  "all-time": "All-Time",
  "current-term": "Current Term",
  active: "Active",
};

function RankingsBlock({ ranks, centuryEligible }: { ranks: CamperRankEntry[]; centuryEligible: boolean }) {
  const visible = ranks.filter((r) => r.rank != null);
  if (visible.length === 0) return null;

  const grouped = new Map<string, CamperRankEntry[]>();
  for (const r of visible) {
    const list = grouped.get(r.metric) ?? [];
    list.push(r);
    grouped.set(r.metric, list);
  }

  return (
    <div className="mt-4 pt-4 border-t border-stone-100 w-full">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide">Rankings</p>
        {centuryEligible && (
          <span className="inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
            Century Club
          </span>
        )}
      </div>
      <div className="space-y-1.5 text-xs">
        {[...grouped.entries()].map(([metric, entries]) => (
          <div key={metric} className="flex justify-between items-baseline gap-2">
            <span className="text-stone-500 shrink-0">{METRIC_LABEL[metric] ?? metric}</span>
            <span className="text-right text-stone-700 tabular-nums">
              {entries.map((e, i) => (
                <span key={`${e.scope}`}>
                  {i > 0 && <span className="text-stone-300 mx-1.5">·</span>}
                  <span className="text-stone-400">{SCOPE_LABEL[e.scope] ?? e.scope}: </span>
                  <span className="font-semibold">{e.rank}</span>
                  <span className="text-stone-400">/{e.total}</span>
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function CamperDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { camper, loading, refetch } = useCamper(Number(id));
  const { user } = useAuth();
  const { selectedTermId } = useSeason();
  const isAdmin = user?.role === "admin";
  const isAdminOrDirector = user?.role === "admin" || user?.role === "director";
  const canSeeRanks = user?.role === "admin" || user?.role === "director" || user?.role === "counselor";

  const [kerchief, setKerchief] = useState<CamperKerchiefData | null>(null);
  const [kerchiefLoading, setKerchiefLoading] = useState(false);
  const [ranks, setRanks] = useState<CamperRankEntry[] | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | "all">("all");

  const [completionOpen, setCompletionOpen] = useState(false);
  const [honorOpen, setHonorOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [addEnrollmentOpen, setAddEnrollmentOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState<Enrollment | null>(null);

  const camperId = camper?.id;

  const loadKerchief = (id: number) => {
    setKerchiefLoading(true);
    reportsApi
      .camperKerchief(id)
      .then(setKerchief)
      .catch(() => toast.error("Failed to load activity data"))
      .finally(() => setKerchiefLoading(false));
  };

  useEffect(() => {
    if (!camperId) return;
    loadKerchief(camperId);
  }, [camperId]);

  useEffect(() => {
    if (!camperId || !canSeeRanks) {
      setRanks(null);
      return;
    }
    rankingsApi
      .camperRanks(camperId, selectedTermId)
      .then((res) => setRanks(res.ranks))
      .catch(() => setRanks(null));
  }, [camperId, selectedTermId, canSeeRanks, kerchief]);

  const handleActivitySuccess = (closeModal: () => void) => {
    closeModal();
    if (camperId) loadKerchief(camperId);
    refetch();
  };

  const handleArchive = async () => {
    if (!camper) return;
    setArchiving(true);
    try {
      await campersApi.archive(camper.id);
      toast.success(camper.archivedAt ? "Camper restored" : "Camper archived");
      setArchiveConfirm(false);
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to archive camper");
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!camper) return;
    setDeleting(true);
    try {
      await campersApi.delete(camper.id);
      toast.success("Camper deleted");
      navigate("/campers");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete camper");
    } finally {
      setDeleting(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!camper) {
    return (
      <div className="p-8 text-center text-stone-500">
        Camper not found.{" "}
        <Link to="/campers" className="text-forest-600 underline">
          Back to campers
        </Link>
      </div>
    );
  }

  const enrollments = [...(camper.enrollments ?? [])].sort((a, b) => b.year - a.year);
  const mostRecent = enrollments[0];
  const enrollmentGroups = buildEnrollmentGroups(camper.enrollments ?? []);
  const enrollmentYears = enrollmentGroups.map((g) => g.year);

  const activities = kerchief?.activities ?? [];

  // Activities with any progress (earned ACs or honors)
  const activeActivities = activities.filter((a) => a.completedAcs > 0 || a.honors.length > 0);

  // Year-filtered activities
  const visibleActivities =
    selectedYear === "all"
      ? activeActivities
      : activeActivities.filter(
          (a) =>
            a.completionYears.some((y) => y === selectedYear) ||
            a.honors.some((h) => h.year === selectedYear)
        );

  // Year-filtered honors only — activities with at least one honor in scope
  const visibleHonors =
    selectedYear === "all"
      ? activeActivities.filter((a) => a.honors.length > 0)
      : activeActivities.filter((a) => a.honors.some((h) => h.year === selectedYear));

  // Profile card totals (all-time). Honors accumulate — sum the counts, not the
  // number of distinct activities with at least one honor.
  const totalEarnedAcs = activities.reduce((sum, a) => sum + a.completedAcs, 0);
  const totalHonors = activities.reduce((sum, a) => sum + a.honors.length, 0);
  const yearHonors = activities.reduce(
    (sum, a) => sum + a.honors.filter((h) => h.year === CURRENT_YEAR).length,
    0
  );

  // Sort: graduated first, then by most ACs earned
  const sortedActivities = [...visibleActivities].sort((a, b) => {
    if (a.graduated !== b.graduated) return a.graduated ? -1 : 1;
    return b.completedAcs - a.completedAcs;
  });

  return (
    <div>
      <PageHeader
        title={`${camper.firstName} ${camper.lastName}`}
        subtitle={mostRecent?.cabin?.name}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Link
              to={`/reports/kerchief?camperId=${camper.id}`}
              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white font-semibold text-sm px-3 py-2 rounded-lg shadow-sm transition-colors backdrop-blur-sm"
            >
              <ReceiptPercentIcon className="w-4 h-4" />
              View Kerchief
            </Link>
            {isAdminOrDirector && (
              <button
                onClick={() => setEditOpen(true)}
                className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white font-semibold text-sm px-3 py-2 rounded-lg shadow-sm transition-colors backdrop-blur-sm"
              >
                <PencilSquareIcon className="w-4 h-4" />
                Edit
              </button>
            )}
            <button
              onClick={() => setCompletionOpen(true)}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-forest-900 font-semibold text-sm px-3 py-2 rounded-lg shadow-sm transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Log AC
            </button>
            <button
              onClick={() => setHonorOpen(true)}
              className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white font-semibold text-sm px-3 py-2 rounded-lg shadow-sm transition-colors"
            >
              <TrophyIcon className="w-4 h-4" />
              Award Honor
            </button>
          </div>
        }
      />

      <div className="p-6 lg:p-8">
        <Link
          to="/campers"
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-6 transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          All Campers
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ─── Profile + Enrollments ─────────────────────────────────── */}
          <div className="lg:col-span-1 space-y-5">
            <Card>
              <div className="flex flex-col items-center text-center py-2">
                <Avatar firstName={camper.firstName} lastName={camper.lastName} size="lg" />
                <h2 className="mt-3 font-display font-bold text-stone-900 text-lg">
                  {camper.firstName} {camper.lastName}
                </h2>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  {(() => {
                    const divisionName =
                      mostRecent?.division?.name ??
                      mostRecent?.cabin?.division?.name ??
                      mostRecent?.cabin?.cabinGroup?.division?.name;
                    return divisionName ? (
                      <Badge label={divisionName} color="green" size="md" />
                    ) : null;
                  })()}
                  {camper.team === "army" && <Badge label="Army" color="army" size="md" />}
                  {camper.team === "navy" && <Badge label="Navy" color="navy" size="md" />}
                </div>
                {camper.linkedUsers && camper.linkedUsers.length > 0 && (
                  <div className="mt-3 w-full">
                    {camper.linkedUsers.map((u) => (
                      <Link
                        key={u.id}
                        to={`/admin/staff?userId=${u.id}`}
                        className="flex items-center justify-center gap-1.5 text-xs font-medium text-forest-700 bg-forest-50 hover:bg-forest-100 border border-forest-200 rounded-full px-2.5 py-1 transition-colors"
                      >
                        <UserIcon className="w-3 h-3" />
                        Now on staff: {u.firstName} {u.lastName}
                      </Link>
                    ))}
                  </div>
                )}
                <dl className="mt-5 w-full text-left space-y-3 text-sm border-t border-stone-100 pt-4">
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Cabin</dt>
                    <dd className="font-medium text-stone-700">
                      {mostRecent?.cabin?.name ?? "—"}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Years Attended</dt>
                    <dd className="font-medium text-stone-700">
                      {enrollmentGroups.length ? enrollmentGroups.map((g) => g.year).join(", ") : "—"}
                    </dd>
                  </div>
                  <div className="flex justify-between items-start">
                    <dt className="text-stone-500 pt-0.5">ACs Earned</dt>
                    <dd className="text-right leading-snug">
                      <span className="font-semibold text-forest-600">{totalEarnedAcs}</span>
                      <span className="text-stone-400 text-xs"> total</span>
                    </dd>
                  </div>
                  <div className="flex justify-between items-start">
                    <dt className="text-stone-500 pt-0.5">Honors</dt>
                    <dd className="text-right leading-snug">
                      <span className="font-semibold text-amber-600">{yearHonors}</span>
                      <span className="text-stone-400 text-xs"> this year</span>
                      <span className="text-stone-300 mx-1">·</span>
                      <span className="font-semibold text-stone-700">{totalHonors}</span>
                      <span className="text-stone-400 text-xs"> total</span>
                    </dd>
                  </div>
                </dl>
                {canSeeRanks && ranks && ranks.some((r) => r.rank != null) && (
                  <RankingsBlock ranks={ranks} centuryEligible={totalEarnedAcs >= 100} />
                )}
              </div>
            </Card>

            {/* Admin actions */}
            {isAdminOrDirector && (
              <Card>
                <CardHeader title="Admin actions" />
                <div className="space-y-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<ArchiveBoxIcon className="w-4 h-4" />}
                    onClick={() => setArchiveConfirm(true)}
                    className="w-full justify-start"
                  >
                    {camper.archivedAt ? "Restore camper" : "Archive camper"}
                  </Button>
                  {isAdmin && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<ArrowsRightLeftIcon className="w-4 h-4" />}
                        onClick={() => setMergeOpen(true)}
                        className="w-full justify-start"
                      >
                        Merge into another camper
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<TrashIcon className="w-4 h-4" />}
                        onClick={() => { setDeleteText(""); setDeleteOpen(true); }}
                        className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Delete camper
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            )}

            {/* Enrollments */}
            <Card>
              <CardHeader
                title="Enrollments"
                actions={
                  isAdminOrDirector && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<PlusIcon className="w-4 h-4" />}
                      onClick={() => setAddEnrollmentOpen(true)}
                    >
                      Add
                    </Button>
                  )
                }
              />
              {enrollmentGroups.length === 0 ? (
                <div className="py-4 text-center text-sm text-stone-400">
                  No enrollments yet.
                </div>
              ) : (
                <div>
                  {enrollmentGroups.map((group) => (
                    <EnrollmentRow
                      key={`${group.year}-${group.isFullTerm ? "full" : group.ids[0]}`}
                      group={group}
                      canEdit={isAdminOrDirector}
                      onClick={(enrollment) => setOverrideTarget(enrollment)}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* ─── Activity cards ────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-5">
            {/* Year tabs */}
            {enrollmentYears.length > 0 && (
              <div className="flex items-center border-b border-stone-200">
                {enrollmentYears.map((year) => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      selectedYear === year
                        ? "border-forest-600 text-forest-700"
                        : "border-transparent text-stone-500 hover:text-stone-700"
                    }`}
                  >
                    {year}
                    {year === CURRENT_YEAR && (
                      <span className="ml-1 text-xs text-stone-400">(current)</span>
                    )}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedYear("all")}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    selectedYear === "all"
                      ? "border-forest-600 text-forest-700"
                      : "border-transparent text-stone-500 hover:text-stone-700"
                  }`}
                >
                  All Time
                </button>
              </div>
            )}

            {/* ACs Earned */}
            <Card>
              <CardHeader
                title="ACs Earned"
                subtitle={
                  selectedYear === "all"
                    ? `${totalEarnedAcs} earned overall`
                    : `Earned in ${selectedYear}`
                }
                actions={
                  <button
                    onClick={() => setCompletionOpen(true)}
                    className="text-xs text-forest-600 hover:text-forest-800 font-medium flex items-center gap-1"
                  >
                    <PlusIcon className="w-3.5 h-3.5" /> Add
                  </button>
                }
              />
              {kerchiefLoading ? (
                <div className="space-y-2 animate-pulse">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-10 bg-stone-100 rounded" />
                  ))}
                </div>
              ) : sortedActivities.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-6">
                  No ACs earned{selectedYear !== "all" ? ` in ${selectedYear}` : " yet"}.
                </p>
              ) : (
                <div>
                  {sortedActivities.map((a) => (
                    <ActivityRow
                      key={a.activity.id}
                      activity={a}
                      selectedYear={selectedYear}
                    />
                  ))}
                </div>
              )}
            </Card>

            {/* Honors */}
            <Card>
              <CardHeader
                title="Honors"
                subtitle={
                  selectedYear === "all"
                    ? `${totalHonors} earned overall`
                    : `${visibleHonors.reduce((s, a) => s + a.honors.filter((h) => h.year === selectedYear).length, 0)} earned in ${selectedYear}`
                }
                actions={
                  <button
                    onClick={() => setHonorOpen(true)}
                    className="text-xs text-forest-600 hover:text-forest-800 font-medium flex items-center gap-1"
                  >
                    <PlusIcon className="w-3.5 h-3.5" /> Add
                  </button>
                }
              />
              {kerchiefLoading ? (
                <div className="space-y-2 animate-pulse">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-10 bg-stone-100 rounded" />
                  ))}
                </div>
              ) : visibleHonors.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-6">
                  No honors{selectedYear !== "all" ? ` in ${selectedYear}` : " yet"}.
                </p>
              ) : (
                <div>
                  {visibleHonors.map((a) => {
                    const inScope =
                      selectedYear === "all"
                        ? a.honors
                        : a.honors.filter((h) => h.year === selectedYear);
                    const years = Array.from(
                      new Set(inScope.map((h) => h.year).filter((y): y is number => y != null))
                    ).sort();
                    return (
                      <div
                        key={a.activity.id}
                        className="flex items-center justify-between py-2.5 border-b border-stone-100 last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <TrophyIcon className="w-5 h-5 text-amber-500" />
                          <span className="text-sm text-stone-700">{a.activity.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedYear === "all" &&
                            years.map((y) => <Badge key={y} label={String(y)} color="stone" />)}
                          <Badge
                            label={inScope.length > 1 ? `${inScope.length}× Honor` : "Honor"}
                            color="amber"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* ─── Modals ──────────────────────────────────────────────────── */}
      <Modal open={completionOpen} onClose={() => setCompletionOpen(false)} title="Log AC" size="md">
        <CompletionForm
          camperId={camper.id}
          onSuccess={() => handleActivitySuccess(() => setCompletionOpen(false))}
        />
      </Modal>

      <Modal open={honorOpen} onClose={() => setHonorOpen(false)} title="Award an Honor" size="md">
        <HonorForm
          camperId={camper.id}
          onSuccess={() => handleActivitySuccess(() => setHonorOpen(false))}
        />
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Camper" size="md">
        <CamperForm
          camper={camper}
          onCancel={() => setEditOpen(false)}
          onSuccess={() => { setEditOpen(false); refetch(); }}
        />
      </Modal>

      <Modal
        open={addEnrollmentOpen}
        onClose={() => setAddEnrollmentOpen(false)}
        title="Add Enrollment"
        size="md"
      >
        <EnrollmentForm
          camperId={camper.id}
          onCancel={() => setAddEnrollmentOpen(false)}
          onSuccess={() => { setAddEnrollmentOpen(false); refetch(); }}
        />
      </Modal>

      <OverrideCabinDialog
        enrollment={overrideTarget}
        open={!!overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onSuccess={() => { setOverrideTarget(null); refetch(); }}
      />

      <Modal open={mergeOpen} onClose={() => setMergeOpen(false)} title="Merge Camper" size="lg">
        <CamperMergeForm
          loser={camper}
          onCancel={() => setMergeOpen(false)}
          onSuccess={(result) => {
            setMergeOpen(false);
            navigate(`/campers/${result.winnerId}`);
          }}
        />
      </Modal>

      <ConfirmDialog
        open={archiveConfirm}
        onClose={() => setArchiveConfirm(false)}
        onConfirm={handleArchive}
        title={camper.archivedAt ? "Restore camper" : "Archive camper"}
        message={
          camper.archivedAt
            ? `Restore ${camper.firstName} ${camper.lastName}?`
            : `Archive ${camper.firstName} ${camper.lastName}? They'll be hidden from default lists but their records will be preserved.`
        }
        confirmLabel={camper.archivedAt ? "Restore" : "Archive"}
        variant={camper.archivedAt ? "primary" : "danger"}
        loading={archiving}
      />

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete Camper" size="md">
        <div className="space-y-4">
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-900">
            <p className="font-semibold">This action cannot be undone.</p>
            <p className="mt-1 text-red-800">
              Deleting will permanently remove this camper. All linked completions and honors will be deleted via cascade.
              Type the camper's full name to confirm.
            </p>
          </div>
          <Input
            label={`Type "${camper.firstName} ${camper.lastName}" to confirm`}
            placeholder={`${camper.firstName} ${camper.lastName}`}
            value={deleteText}
            onChange={(e) => setDeleteText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              loading={deleting}
              disabled={deleteText.trim() !== `${camper.firstName} ${camper.lastName}`}
              onClick={handleDelete}
            >
              Delete forever
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Activity row ─────────────────────────────────────────────────────────────

function ActivityRow({
  activity: a,
  selectedYear,
}: {
  activity: KerchiefActivity;
  selectedYear: number | "all";
}) {
  const [open, setOpen] = useState(false);

  const earnedThisYear =
    selectedYear !== "all"
      ? a.completionYears.filter((y) => y === selectedYear).length
      : a.completedAcs;

  return (
    <div className="border-b border-stone-100 last:border-0">
      {/* Header row — clickable */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-2.5 text-left hover:bg-stone-50 transition-colors px-1 -mx-1 rounded"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-stone-400 flex-shrink-0">
            {open
              ? <ChevronDownIcon className="w-3.5 h-3.5" />
              : <ChevronRightIcon className="w-3.5 h-3.5" />}
          </span>
          <span className="text-stone-400 flex-shrink-0">
            <Square2StackIcon className="w-4 h-4 flex-shrink-0" />
          </span>
          <span className="text-sm text-stone-700 truncate">{a.activity.name}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {a.graduated ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-forest-700 bg-forest-50 border border-forest-200 rounded-full px-2 py-0.5">
              <StarIcon className="w-3 h-3" />
              Graduated
            </span>
          ) : (
            <span className="text-xs text-stone-500">
              {selectedYear === "all"
                ? `${a.completedAcs}/${a.totalAcs}`
                : `${earnedThisYear}/${a.totalAcs}`}{" "}
              ACs
            </span>
          )}
          {a.honors.length > 0 && (
            <Badge
              label={a.honors.length > 1 ? `${a.honors.length}× Honor` : "Honor"}
              color="amber"
            />
          )}
        </div>
      </button>

      {/* Expanded AC list */}
      {open && (
        <div className="pb-2 pl-10 pr-1">
          {a.acs.length === 0 ? (
            <p className="text-xs text-stone-400 py-1">No ACs defined for this activity.</p>
          ) : (
            <div className="space-y-1">
              {sortAcs(a.acs).map((ac) => (
                <div key={ac.id} className="flex items-center gap-2">
                  {ac.completed ? (
                    <CheckCircleIcon className="w-4 h-4 text-forest-500 flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-stone-300 flex-shrink-0" />
                  )}
                  <span className={`text-xs ${ac.completed ? "text-stone-700" : "text-stone-400"}`}>
                    {ac.name}
                  </span>
                  {ac.completed && ac.completedDate && (
                    <span className="text-xs text-stone-400 ml-auto tabular-nums">
                      {new Date(ac.completedDate).getFullYear()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Enrollment row ───────────────────────────────────────────────────────────

interface EnrollmentRowProps {
  group: EnrollmentGroup;
  canEdit: boolean;
  onClick: (enrollment: Enrollment) => void;
}

function EnrollmentRow({ group, canEdit, onClick }: EnrollmentRowProps) {
  // Full-term groups have a per-term override on each underlying enrollment;
  // render a header plus one clickable sub-row per term so admins can move a
  // camper for 1st term without touching 2nd term (or vice versa).
  if (group.isFullTerm && group.termEnrollments && group.termEnrollments.length > 0) {
    return (
      <div className="border-b border-stone-100 last:border-0 py-2">
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="w-4 h-4 text-stone-400 shrink-0" />
          <span className="text-sm font-semibold text-stone-800 shrink-0">{group.year}</span>
          <span className="text-sm text-forest-600 font-medium truncate">{group.termLabel}</span>
        </div>
        <div className="mt-1 ml-6 space-y-0.5">
          {group.termEnrollments.map((enr) => (
            <FullTermSubRow
              key={enr.id}
              enrollment={enr}
              canEdit={canEdit}
              onClick={onClick}
            />
          ))}
        </div>
      </div>
    );
  }

  // The override lives on a single Enrollment row, not on the year-level
  // grouping. The CampMinder/manual cabin underneath stays untouched.
  const target = group.singleEnrollment;
  const overrideCabin = target?.overrideCabin;
  const baseName = group.cabin?.name ? formatCabinNumber(group.cabin.name) : null;
  const overrideName = overrideCabin?.name ? formatCabinNumber(overrideCabin.name) : null;
  const clickable = canEdit && !!target;

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick(target) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(target);
              }
            }
          : undefined
      }
      className={`flex items-center py-2.5 border-b border-stone-100 last:border-0 gap-2 ${
        clickable ? "cursor-pointer hover:bg-stone-50 -mx-2 px-2 rounded" : ""
      }`}
      title={clickable ? "Click to override cabin" : undefined}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <CalendarDaysIcon className="w-4 h-4 text-stone-400 shrink-0" />
        <span className="text-sm font-semibold text-stone-800 shrink-0">{group.year}</span>
        <span className={`text-sm truncate ${group.isFullTerm ? "text-forest-600 font-medium" : "text-stone-500"}`}>
          {group.termLabel}
        </span>
        {baseName && (
          <span className="text-xs text-stone-400 truncate">· {baseName}</span>
        )}
        {overrideName && (
          <span className="text-xs font-medium text-amber-600 truncate">
            → {overrideName} (override)
          </span>
        )}
      </div>
    </div>
  );
}

interface FullTermSubRowProps {
  enrollment: Enrollment;
  canEdit: boolean;
  onClick: (enrollment: Enrollment) => void;
}

function FullTermSubRow({ enrollment, canEdit, onClick }: FullTermSubRowProps) {
  const baseName = enrollment.cabin?.name ? formatCabinNumber(enrollment.cabin.name) : null;
  const overrideName = enrollment.overrideCabin?.name
    ? formatCabinNumber(enrollment.overrideCabin.name)
    : null;
  const termLabel = enrollment.term?.name ?? "Term";

  return (
    <div
      role={canEdit ? "button" : undefined}
      tabIndex={canEdit ? 0 : undefined}
      onClick={canEdit ? () => onClick(enrollment) : undefined}
      onKeyDown={
        canEdit
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(enrollment);
              }
            }
          : undefined
      }
      className={`flex items-center gap-2 text-xs py-1 ${
        canEdit ? "cursor-pointer hover:bg-stone-50 -mx-2 px-2 rounded" : ""
      }`}
      title={canEdit ? "Click to override cabin" : undefined}
    >
      <span className="text-stone-500 truncate">{termLabel}</span>
      {baseName && <span className="text-stone-400 truncate">· {baseName}</span>}
      {overrideName && (
        <span className="font-medium text-amber-600 truncate">
          → {overrideName} (override)
        </span>
      )}
    </div>
  );
}
